// Misc Module
// For things that don't fit in other modules
import FirebaseAPI from '@/api/firebase'
import ForeplayAPI from '@/api/foreplayServer'

// Ad card elements shown when the user hasn't picked any yet
const DEFAULT_AD_SETTINGS = [{ name: 'Brand Details', value: 'Brand Details' }]

export default {
  namespaced: true,
  state: {
    // Controls the collapse state of the sidebar
    sidebarCollapsed: false,
    // Sets the transition duration of the sidebar collapse
    sidebarCollapseDuration: 250,
    // Display control for the settings drawer (string or null for closed)
    settingsDrawerViewMode: null,
    // A guard that warns users when leaving a settings page without saving changes (warning message string or null for no guard)
    settingsDrawerSaveGuard: null,
    // Display control for the subscription drawer (manage & create)
    subscriptionDrawerViewMode: { state: null, initInterval: null },
    // App style (this is set in the router)
    theme: 'blue',
    lastTheme: 'blue',
    themeChangedAt: 0,
    discoveryAdCount: 0,
    preventDrawerEscapeKey: false,
    // Beta Users
    betaUsers: [],
    // Controls whether to display sensitive data in the UI
    isPrivateMode: false,
    // Featurebase Unread Changelogs Count
    unreadChangelogCount: 0,
    // Version for display in API views
    apiVersion: null,
    // Session-local ad display settings (which elements show on LibraryCards).
    localAdSettings: null,
    // Group Meta ads that share a creative into one card (persisted per device)
    uniqueCreatives: localStorage.getItem('uniqueCreatives') !== 'false',
    // Where Spyder brands open: 'overview' or 'meta' (persisted per device)
    brandOpenView: localStorage.getItem('brandOpenView') === 'meta' ? 'meta' : 'overview'
  },
  getters: {
    getSidebarCollapsed: state => state.sidebarCollapsed,
    getSidebarCollapseDuration: state => state.sidebarCollapseDuration,
    getSettingsDrawerViewMode: state => state.settingsDrawerViewMode,
    getSettingsDrawerSaveGuard: state => state.settingsDrawerSaveGuard,
    getSubscriptionDrawerViewMode: state => state.subscriptionDrawerViewMode?.state,
    getSubscriptionDrawerInterval: state => state.subscriptionDrawerViewMode?.initInterval,
    // Get Theme color
    getTheme: state => state.theme,
    // Get the previous theme to transtion from
    getLastTheme: state => state.lastTheme,
    // Last time the theme was updated
    getThemeChangedAt: state => state.themeChangedAt,
    // Get UTM_source UTM_medium UTM_campaign for tracking purposes
    getUtmSource: state => state.utmSource,
    getUtmMedium: state => state.utmMedium,
    getUtmCampaign: state => state.utmCampaign,
    getDiscoveryAdCount: state => state.discoveryAdCount,
    getPreventDrawerEscapeKey: state => state.preventDrawerEscapeKey,
    getBetaUsers: state => state.betaUsers,
    getIsPrivateMode: state => state.isPrivateMode,
    getUnreadChangelogCount: state => state.unreadChangelogCount,
    getApiVersion: state => state.apiVersion,
    getAdSettings: (state, getters, rootState, rootGetters) => {
      const user = rootGetters['AuthModule/getUser']
      const settings = user?.user_id ? user.adSettings : state.localAdSettings
      return settings || DEFAULT_AD_SETTINGS
    },
    getUniqueCreatives: state => state.uniqueCreatives,
    getBrandOpenView: state => state.brandOpenView
  },
  mutations: {
    SET_SIDEBAR_COLLAPSED (state, collapsed) {
      state.sidebarCollapsed = collapsed
    },
    SET_SETTINGS_DRAWER_VIEW_MODE (state, mode) {
      state.settingsDrawerViewMode = mode
    },
    SET_SETTINGS_DRAWER_SAVE_GUARD (state, guard) {
      state.settingsDrawerSaveGuard = guard
    },
    SET_SUBSCRIPTION_DRAWER_VIEW_MODE (state, view) {
      const {state: mode, initInterval = null} = view
      state.subscriptionDrawerViewMode.state = mode
      state.subscriptionDrawerViewMode.initInterval = mode === null ? null : initInterval
    },
    SET_THEME (state, theme) {
      console.log('setting theme')
      state.themeChangedAt = +new Date()
      state.lastTheme = state.theme
      state.theme = theme
    },
    SET_UTM_SOURCE (state, utmSource) {
      state.utmSource = utmSource
    },
    SET_UTM_MEDIUM (state, utmMedium) {
      state.utmMedium = utmMedium
    },
    SET_UTM_CAMPAIGN (state, utmCampaign) {
      state.utmCampaign = utmCampaign
    },
    SET_DISCOVERY_AD_COUNT (state, count) {
      state.discoveryAdCount = count
    },
    SET_PREVENT_DRAWER_ESCAPE_KEY (state, doPrevent) {
      state.preventDrawerEscapeKey = doPrevent
    },
    SET_BETA_USERS (state, users) {
      state.betaUsers = users
    },
    SET_IS_PRIVATE_MODE (state, isPrivate) {
      state.isPrivateMode = isPrivate
    },
    SET_UNREAD_CHANGELOG_COUNT (state, count) {
      state.unreadChangelogCount = count
    },
    SET_API_VERSION (state, version) {
      state.apiVersion = version
    },
    SET_LOCAL_AD_SETTINGS (state, settings) {
      state.localAdSettings = settings
    },
    SET_UNIQUE_CREATIVES (state, enabled) {
      state.uniqueCreatives = enabled
    },
    SET_BRAND_OPEN_VIEW (state, view) {
      state.brandOpenView = view
    }
  },
  actions: {
    // ================================================================================
    // ============================== USER PREFERENCES ================================
    // ================================================================================
    // Shared by Settings > Preferences and the in-app toggles so both stay in sync

    setPrivateMode ({ commit }, isPrivate) {
      commit('SET_IS_PRIVATE_MODE', isPrivate)
      localStorage.setItem('privateMode', String(isPrivate))
    },
    setUniqueCreatives ({ commit }, enabled) {
      commit('SET_UNIQUE_CREATIVES', enabled)
      localStorage.setItem('uniqueCreatives', String(enabled))
    },
    setBrandOpenView ({ commit }, view) {
      commit('SET_BRAND_OPEN_VIEW', view)
      localStorage.setItem('brandOpenView', view)
    },
    async updateAdSettings ({ commit, rootGetters }, settings) {
      commit('SET_LOCAL_AD_SETTINGS', settings)

      // Only persist to the user's profile when they're signed in
      const user = rootGetters['AuthModule/getUser']
      if (user?.user_id) {
        const previousSettings = user.adSettings
        commit('AuthModule/SET_USER', { ...user, adSettings: settings }, { root: true })
        try {
          await FirebaseAPI.Users.update(user.user_id, { adSettings: settings })
        } catch (err) {
          console.log(err)
          commit('AuthModule/SET_USER', { ...rootGetters['AuthModule/getUser'], adSettings: previousSettings }, { root: true })
        }
      }

      // Ad cards change height, so let the grids reflow
      setTimeout(() => { window.dispatchEvent(new Event('resize')) }, 100)
    },
    toggleAdSetting ({ getters, dispatch }, setting) {
      const current = getters.getAdSettings
      const updated = current.some(s => s.value === setting)
        ? current.filter(s => s.value !== setting)
        : [...current, { name: setting, value: setting }]
      return dispatch('updateAdSettings', updated)
    },

    async fetchBetaUsers ({ commit }) {
      this.betaIds = await FirebaseAPI.Beta.getBetaUserIds()
      // console.log('beta ids', this.betaIds)
      commit('SET_BETA_USERS', this.betaIds)
    },
    async fetchApiVersion ({ commit }) {
      const apiInfo = await ForeplayAPI.PublicAPI.getAPIInfo()
      commit('SET_API_VERSION', apiInfo?.version ?? null)
    }
  }
}
